import { Button, Icon } from '@/shared/components'
import { useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons'
import { Divider, Grid, styled, Tooltip, Typography } from '@mui/material'

import { useWidgetToolbarContext } from './WidgetToolbarContext'

export type WidgetToolbarSettingsProps = {
  //
}

export const WidgetToolbarSettings: React.FC<WidgetToolbarSettingsProps> = () => {
  const { isWidgetSettingsOpen, widgets, hideWidget, unHideWidget, setWidgetSettingsOpen } = useWidgetToolbarContext()
  const userName = useAppSelector(state => state.user?.name)
  const hiddenWidgets = widgets.filter(widget => widget.location === 'hidden')
  const addedWidgets = widgets.filter(widget => widget.location !== 'hidden')

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h1">Add or Remove Widgets</Typography>
        <StyledHelperTypography variant="body1">Customise widget tools {userName && `for ${userName}`}</StyledHelperTypography>
      </StyledHeader>
      <Divider />
      <StyledGridContent container direction="column">
        <Grid item>
          <StyledSectionTypography variant="body1">Available widgets to add</StyledSectionTypography>
          {hiddenWidgets.map((widget, idx) => (
            <StyledCard key={idx} container direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <StyledCardName item>
                <Typography variant="h2">{widget.name}</Typography>
                <Tooltip arrow title={widget.description}>
                  <div>
                    <StyledHelperIcon icon={faQuestionCircle} />
                  </div>
                </Tooltip>
              </StyledCardName>
              <Grid item>
                <Button text round onClick={() => unHideWidget(widget.widgetType)}>
                  Add
                </Button>
              </Grid>
            </StyledCard>))}
          {hiddenWidgets.length === 0 && <StyledHelperTypography variant="body1">No widgets available to add</StyledHelperTypography>}
        </Grid>
        <Grid item sx={{ marginTop: pxToRem(16) }}>
          <StyledSectionTypography variant="body1">Added widgets</StyledSectionTypography>
          {addedWidgets.map((widget, idx) => (
            <StyledCard key={idx} container direction={'row'} justifyContent={'space-between'}>
              <StyledCardName item >
                <Typography variant="h2">{widget.name}</Typography>
                <Tooltip arrow title={widget.description}>
                  <div>
                    <StyledHelperIcon icon={faQuestionCircle} />
                  </div>
                </Tooltip>
              </StyledCardName>
              <Grid item>
                <Button text round onClick={() => hideWidget(widget.widgetType)}>
                  Remove
                </Button>
              </Grid>
            </StyledCard>))}
          {addedWidgets.length === 0 && <StyledHelperTypography variant="body1">No widgets on the toolbar</StyledHelperTypography>}
        </Grid>
      </StyledGridContent>
      {isWidgetSettingsOpen && <StyledActions>
        <Button text onClick={() => { setWidgetSettingsOpen(false) }}>Cancel</Button>
        <Button primary onClick={() => { setWidgetSettingsOpen(false) }}>Save</Button>
      </StyledActions>}
    </StyledContainer>
  )
}


const StyledContainer = styled('div')`
  position: relative;
  background-color: ${({ theme }) => theme.palette.background.paper};
  height: calc(100% - ${pxToRem(16)});
  width: 100%;
  box-shadow: ${({ theme }) => theme.palette.custom.widgetToolbar.boxShadow};
  display: flex;
  flex-direction: column;
  padding: 0 0 ${pxToRem(8)} 0;
  margin: ${pxToRem(4)} 0 ${pxToRem(4)} 0;
`

const StyledHeader = styled('div')`
  background-color: ${({ theme }) => theme.palette.custom.mosaicWindowToolbar.backgroundColor};
  padding: ${pxToRem(8)};
`

const StyledHelperTypography = styled(Typography)`
  margin-top: ${pxToRem(4)};
  color: ${({ theme }) => theme.palette.text.secondary};
`

const StyledGridContent = styled(Grid)`
  padding: ${pxToRem(8)};
`

const StyledSectionTypography = styled(Typography)`
  font-size: ${pxToRem(16)};
  margin-bottom: ${pxToRem(8)};
`

const StyledCard = styled(Grid)`
  padding: ${pxToRem(8)};
  border-radius: ${pxToRem(4)};
  background-color: ${({ theme }) => theme.palette.custom.widgetButton.backgroundColor};
  margin-bottom: ${pxToRem(8)};
`

const StyledCardName = styled(Grid)`
  display: flex;
  justify-content: row;
  align-items: center;
`

const StyledHelperIcon = styled(Icon)`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.pxToRem(16)};
  margin-left: ${({ theme }) => theme.typography.pxToRem(6)};
`

const StyledActions = styled('div')`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  margin: ${(props) => props.theme.typography.pxToRem(16)} ${(props) => props.theme.typography.pxToRem(8)};
`